import { DateTime } from "luxon";

/** Same options as the dashboard Notifications card (`notificationBoxRangeControl`). */
export type NotificationDashboardRange =
  | "today"
  | "all"
  | 7
  | 14
  | 30
  | "this_week"
  | "last_week";

/**
 * Matches dashboard `getDateRangeForPeriodInLocal` — used for notification counts and Patients list filtering.
 */
export function getNotificationDashboardDateRangeInLocal(
  range: NotificationDashboardRange
): { start: DateTime; end: DateTime } {
  const now = DateTime.now();
  const startOfToday = now.startOf("day");
  const endOfToday = now.endOf("day");
  if (range === "all") {
    // "All" means no date filter; callers should normally omit dates instead of using this.
    // Return a very wide window as a safe fallback.
    return { start: now.minus({ years: 10 }).startOf("day"), end: endOfToday };
  }
  if (range === "today") {
    return { start: startOfToday, end: endOfToday };
  }
  if (range === "this_week") {
    const daysBackToSunday = now.weekday === 7 ? 0 : now.weekday;
    const lastSunday = now.minus({ days: daysBackToSunday }).startOf("day");
    const thisSaturday = lastSunday.plus({ days: 6 }).endOf("day");
    return { start: lastSunday, end: thisSaturday };
  }
  if (range === "last_week") {
    const daysBackToSunday = now.weekday === 7 ? 0 : now.weekday;
    const lastSunday = now.minus({ days: daysBackToSunday }).startOf("day");
    const lastWeekSunday = lastSunday.minus({ days: 7 });
    const lastWeekSaturday = lastWeekSunday.plus({ days: 6 }).endOf("day");
    return { start: lastWeekSunday, end: lastWeekSaturday };
  }
  const start = startOfToday.minus({ days: range - 1 });
  return { start, end: endOfToday };
}

/** Parse query param string (e.g. `"7"`, `"today"`) into a known range. */
export function parseNotificationDashboardRange(
  raw: string | null | undefined
): NotificationDashboardRange | null {
  if (raw == null || !String(raw).trim()) {
    return null;
  }
  const s = String(raw).trim().toLowerCase();
  if (s === "all") {
    return "all";
  }
  if (s === "today") {
    return "today";
  }
  if (s === "this_week") {
    return "this_week";
  }
  if (s === "last_week") {
    return "last_week";
  }
  const n = Number(s);
  if (n === 7 || n === 14 || n === 30) {
    return n as 7 | 14 | 30;
  }
  return null;
}
